import React from 'react'
import { useTranslation } from 'react-i18next'
import { BestSellingProduct, CSBestSellingProduct } from '@/types'
import { Package } from 'lucide-react'
import SectionTitle from './SectionTitle'

interface MostSoldItemsTableProps {
  products: BestSellingProduct[] | CSBestSellingProduct[]
  formatNumber: (num: number | string | undefined | null) => string
  fullWidth?: boolean
  isLoading?: boolean
}

// LTR Number component (matching Flutter's Directionality)
const LTRNum: React.FC<{
  text: string
  style?: React.CSSProperties
}> = ({ text, style }) => {
  return (
    <span style={{ direction: 'ltr', display: 'inline-block', ...style }}>
      {text}
    </span>
  )
}

const MostSoldItemsTable: React.FC<MostSoldItemsTableProps> = ({
  products,
  formatNumber,
  fullWidth = false,
  isLoading = false,
}) => {
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl p-4 flex flex-col gap-4">
        <SectionTitle titleKey="best_selling_products" />
        <div className="py-3 flex justify-center">
          <div className="w-6 h-6 border-2 border-gray-300 border-t-brand-base rounded-full animate-spin" />
        </div>
      </div>
    )
  }

  if (!products || products.length === 0) {
    return (
      <div className="bg-white rounded-2xl p-4 flex flex-col gap-4">
        <SectionTitle titleKey="best_selling_products" />
        <div className="py-3 text-center text-gray-500 font-['IBM_Plex_Sans_Arabic']">
          {t('no_products')}
        </div>
      </div>
    )
  }

  return (
    <div
      className={`bg-white rounded-2xl p-4 flex flex-col gap-2.5 ${fullWidth ? 'lg:col-span-2' : ''}`}
    >
      <SectionTitle titleKey="best_selling_products" />
      <div className="h-2.5" /> {/* SizedBox(height: 10) */}

      {/* Header Bar */}
      <div
        className="h-9 px-2.5 rounded-[10px] flex items-center"
        style={{ backgroundColor: '#F5F5F5' }}
      >
        <div className="flex-[2] text-start text-sm font-normal font-['IBM_Plex_Sans_Arabic']">
          {t('item_name')}
        </div>
        <div className="flex-1 text-center text-sm font-normal font-['IBM_Plex_Sans_Arabic']">
          {t('order_count')}
        </div>
      </div>

      <div className="h-1.5" /> {/* SizedBox(height: 6) */}

      {/* Products List */}
      <div className="flex flex-col gap-1">
        {products.map((product, index) => (
          <div
            key={index}
            className="px-0.5 py-0.5 rounded-xl"
            style={{
              backgroundColor: index % 2 === 0 ? '#F5F5F5' : 'white',
            }}
          >
            <div className="flex items-center">
              {/* Item Name (flex: 2) */}
              <div className="flex-[2] flex items-center gap-2">
                <div className="relative w-7 h-7 rounded-md overflow-hidden">
                  {product.productImage ? (
                    <img
                      src={product.productImage}
                      alt={product.productName}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                        const parent = target.parentElement
                        if (parent) {
                          parent.innerHTML = ''
                          parent.className = 'w-7 h-7 rounded-md flex items-center justify-center'
                          parent.style.backgroundColor = '#F5F5F5'
                          const icon = document.createElement('div')
                          icon.innerHTML = '<svg class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path></svg>'
                          parent.appendChild(icon)
                        }
                      }}
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-100 rounded-md flex items-center justify-center">
                      <Package className="w-4 h-4 text-gray-500" />
                    </div>
                  )}
                </div>
                <span className="flex-1 font-medium font-['IBM_Plex_Sans_Arabic'] text-[13px] truncate">
                  {product.productName}
                </span>
              </div>

              {/* Order Count (flex: 1) */}
              <div className="flex-1 flex justify-center">
                <LTRNum
                  text={formatNumber(product.totalQuantity)}
                  style={{
                    fontWeight: 500,
                    fontFamily: 'IBM_Plex_Sans_Arabic',
                    fontSize: '13px',
                    color: '#000000',
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default MostSoldItemsTable
